import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, ChevronDown, Plus, Trash2, Download, Upload } from "lucide-react";
import { api, apiError, downloadFile } from "@/lib/api";
import { fmtNum, fmtMoney, fmtDate, today } from "@/lib/format";
import { Kpi, Panel, Badge2, Bar, PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFormDraft } from "@/lib/drafts";
import { DraftPrompt, DraftRestoredBanner } from "@/components/DraftPrompt";

const BLANK = {
  code: "", name: "", parent_id: "", unit: "MT", total_qty: 0, rate: 0, productivity: 0,
  discipline: "", planned_start: today(), planned_finish: today(), predecessors: [],
};

const UNITS = ["Nos", "MT", "Cu.M", "Sq.M", "RMT", "MW", "Dia-Inch", "Cable Mtr", "Modules", "Kg", "Ltr"];

export default function WBS() {
  const { pid } = useParams();
  const [rows, setRows] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const draft = useFormDraft({
    formId: `wbs:${pid}`, form, setForm, blank: BLANK, onRestore: () => setOpen(true),
  });

  const load = () => api.get(`/projects/${pid}/wbs`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [pid]);

  const tree = useMemo(() => {
    const byParent = {};
    rows.forEach((r) => { (byParent[r.parent_id || "root"] ||= []).push(r); });
    const out = [];
    const walk = (parent, depth) => {
      (byParent[parent] || []).forEach((r) => {
        out.push({ ...r, depth, hasKids: !!byParent[r.id] });
        if (!collapsed[r.id]) walk(r.id, depth + 1);
      });
    };
    walk("root", 0);
    return out;
  }, [rows, collapsed]);

  const totals = useMemo(() => {
    const leaves = rows.filter((r) => !rows.some((x) => x.parent_id === r.id));
    return {
      budget: leaves.reduce((s, r) => s + (r.budget || 0), 0),
      earned: leaves.reduce((s, r) => s + (r.executed_qty || 0) * (r.rate || 0), 0),
      manhours: leaves.reduce((s, r) => s + (r.manhours_required || 0), 0),
      count: rows.length,
      critical: rows.filter((r) => r.critical).length,
    };
  }, [rows]);

  const create = async () => {
    try {
      await api.post("/activities", {
        project_id: pid, ...form, parent_id: form.parent_id || null,
        level: form.parent_id ? 3 : 2,
        total_qty: Number(form.total_qty), rate: Number(form.rate), productivity: Number(form.productivity),
      });
      toast.success("Activity added to WBS");
      setOpen(false);
      setForm(BLANK);
      draft.clear();
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/activities/${id}`); toast.success("Activity removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const headers = ["Code", "Activity", "Unit", "Scope Qty", "Executed", "Balance", "Progress %", "Run-Rate/day", "Rate", "Budget", "Total Float"];
    const lines = [headers.join(",")].concat(
      rows.map((r) => [r.code, `"${r.name}"`, r.unit, r.total_qty, r.executed_qty, r.balance_qty, r.progress_pct, r.run_rate_required, r.rate, r.budget, r.total_float].join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "wbs_quantities.csv";
    a.click();
  };

  return (
    <div data-testid="wbs-page">
      <PageHead title="WBS & Quantities Engine" subtitle="Balance quantity, progress % and required run-rate recalculated from validated DPRs.">
        <Button variant="outline" onClick={exportCsv} data-testid="wbs-export-csv-btn"><Download className="w-4 h-4 mr-1.5" /> CSV</Button>
        <Button variant="outline" onClick={() => downloadFile(`/reports/excel/${pid}`, "wbs_pack.xlsx")} data-testid="wbs-export-xlsx-btn"><Upload className="w-4 h-4 mr-1.5" /> Excel pack</Button>
        <Button onClick={() => setOpen(true)} data-testid="wbs-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Add activity</Button>
      </PageHead>

      <DraftPrompt draft={draft} label="WBS activity" />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
        <Kpi label="WBS elements" value={totals.count} testId="kpi-wbs-count" />
        <Kpi label="Scope value" value={fmtMoney(totals.budget)} tone="primary" />
        <Kpi label="Earned value" value={fmtMoney(totals.earned)} tone="good" />
        <Kpi label="Manhours required" value={fmtNum(totals.manhours, 0)} />
        <Kpi label="Critical activities" value={totals.critical} tone={totals.critical ? "warn" : "good"} />
      </div>

      <Panel title="Scope Breakdown Structure (collapsible tree-table)" testId="wbs-tree-panel">
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full dense-table">
            <thead className="sticky-head">
              <tr>
                <th className="text-left min-w-[300px]">Code / Activity</th>
                <th>Unit</th>
                <th className="text-right">Scope Qty</th>
                <th className="text-right">Executed</th>
                <th className="text-right">Balance</th>
                <th>Progress</th>
                <th className="text-right">Run-Rate Req.</th>
                <th className="text-right">Rate</th>
                <th className="text-right">Budget</th>
                <th className="text-right">Float</th>
                <th>Window</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tree.map((r) => (
                <tr key={r.id} data-testid={`wbs-row-${r.code}`}>
                  <td>
                    <div className="flex items-center gap-1.5" style={{ paddingLeft: r.depth * 16 }}>
                      {r.hasKids ? (
                        <button onClick={() => setCollapsed((c) => ({ ...c, [r.id]: !c[r.id] }))} data-testid={`wbs-toggle-${r.code}`}>
                          {collapsed[r.id] ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </button>
                      ) : <span className="w-3.5" />}
                      <span className="num text-[11px] text-muted-foreground">{r.code}</span>
                      <span className={r.hasKids ? "font-semibold" : ""}>{r.name}</span>
                      {r.critical ? <Badge2 tone="bad">Critical</Badge2> : null}
                    </div>
                  </td>
                  <td className="text-muted-foreground">{r.unit || "—"}</td>
                  <td className="num text-right">{r.total_qty ? fmtNum(r.total_qty, 0) : "—"}</td>
                  <td className="num text-right text-emerald-500">{r.total_qty ? fmtNum(r.executed_qty, 0) : "—"}</td>
                  <td className="num text-right text-amber-500">{r.total_qty ? fmtNum(r.balance_qty, 0) : "—"}</td>
                  <td><Bar pct={r.progress_pct} /></td>
                  <td className="num text-right">{r.total_qty ? fmtNum(r.run_rate_required, 1) : "—"}</td>
                  <td className="num text-right text-muted-foreground">{r.rate ? fmtNum(r.rate, 0) : "—"}</td>
                  <td className="num text-right">{r.budget ? fmtMoney(r.budget) : "—"}</td>
                  <td className="num text-right">{r.total_float ?? "—"}</td>
                  <td className="text-[11px] text-muted-foreground">{r.planned_start ? `${fmtDate(r.planned_start)} → ${fmtDate(r.planned_finish)}` : "—"}</td>
                  <td>
                    <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-red-500 transition-colors duration-150" data-testid={`wbs-delete-${r.code}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Add WBS activity</DialogTitle></DialogHeader>
          <DraftRestoredBanner draft={draft} />
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label className="text-xs uppercase">Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="activity-code-input" /></div>
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs uppercase">Activity name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="activity-name-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Parent</Label>
              <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="activity-parent-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Top level —</SelectItem>
                  {rows.filter((r) => !r.parent_id).map((r) => <SelectItem key={r.id} value={r.id}>{r.code} {r.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="activity-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select></div>
            {[["total_qty", "Scope qty"], ["rate", "Rate (₹/unit)"], ["productivity", "Manhours / unit"], ["discipline", "Discipline"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5"><Label className="text-xs uppercase">{l}</Label>
                <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`activity-${k}-input`} /></div>
            ))}
            <div className="space-y-1.5"><Label className="text-xs uppercase">Planned start</Label>
              <Input type="date" value={form.planned_start} onChange={(e) => setForm({ ...form, planned_start: e.target.value })} data-testid="activity-start-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Planned finish</Label>
              <Input type="date" value={form.planned_finish} onChange={(e) => setForm({ ...form, planned_finish: e.target.value })} data-testid="activity-finish-input" /></div>
            <div className="space-y-1.5 sm:col-span-3"><Label className="text-xs uppercase">Predecessor (single)</Label>
              <Select value={form.predecessors[0] || "none"} onValueChange={(v) => setForm({ ...form, predecessors: v === "none" ? [] : [v] })}>
                <SelectTrigger data-testid="activity-predecessor-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {rows.filter((r) => r.parent_id).map((r) => <SelectItem key={r.id} value={r.id}>{r.code} {r.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
          <DialogFooter><Button onClick={create} disabled={!form.code || !form.name} data-testid="activity-save-btn">Save activity</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
